import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  TextField,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  Inventory as InventoryIcon,
  Event as EventIcon,
  MenuBook as RecipeIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { computeCommunityStats } from '../../utils/adminStats';

interface CategoryStats {
  category: string;
  count: number;
}

interface WordStats {
  word: string;
  count: number;
}

interface ItemUsage {
  itemName: string;
  usageCount: number;
  category: string;
}

interface RecipeUsage {
  recipeName: string;
  usageCount: number;
}

interface GlobalStats {
  totalInventory: number;
  totalEvents: number;
  totalRecipes: number;
  avgInventoryPerUser: number;
  avgEventsPerUser: number;
  avgRecipesPerUser: number;
  categoryStats: CategoryStats[];
  wordStats: WordStats[];
  topInventoryItems: ItemUsage[];
  topRecipes: RecipeUsage[];
}

export const AdminStats: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<GlobalStats | null>(null);
  const [categorySearch, setCategorySearch] = useState('');
  const [wordSearch, setWordSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [raw, setRaw] = useState<{ inventory: any[]; events: any[]; recipes: any[] } | null>(null);

  useEffect(() => {
    fetchStats();
  }, []);

  // Recomputa as estatísticas ao mudar o período, sem refazer a busca.
  useEffect(() => {
    if (!raw) return;
    setStats(computeCommunityStats({ ...raw, from: dateFrom, to: dateTo }));
  }, [raw, dateFrom, dateTo]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const [inventorySnapshot, eventsSnapshot, recipesSnapshot] = await Promise.all([
        getDocs(collection(db, 'inventoryItems')),
        getDocs(collection(db, 'events')),
        getDocs(collection(db, 'recipes')),
      ]);

      const inventory = inventorySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      const events = eventsSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      const recipes = recipesSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

      setRaw({ inventory, events, recipes });
    } catch (err: any) {
      console.error('Erro ao buscar estatísticas:', err);
      setError(err.message || 'Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress aria-label="Carregando" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!stats) {
    return null;
  }

  const filteredCategories = stats.categoryStats.filter(cat =>
    cat.category.toLowerCase().includes(categorySearch.toLowerCase())
  );

  const filteredWords = stats.wordStats.filter(word =>
    word.word.toLowerCase().includes(wordSearch.toLowerCase())
  );

  return (
    <Box sx={{ px: 3 }}>
      {/* Filtro de período (por data de criação) */}
      <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <TextField
          label="De"
          type="date"
          size="small"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Até"
          type="date"
          size="small"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        {(dateFrom || dateTo) ? (
          <Typography
            component="button"
            onClick={() => { setDateFrom(''); setDateTo(''); }}
            sx={{ border: 'none', background: 'none', cursor: 'pointer', color: 'primary.main', fontSize: '0.85rem' }}
          >
            Limpar
          </Typography>
        ) : (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Filtra por data de criação. Vazio = todo o período.
          </Typography>
        )}
      </Box>

      {/* Estatísticas Globais da Comunidade */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: 'text.primary' }}>
          Estatísticas da Comunidade Hostly
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Card elevation={0} sx={{
            flex: '1 1 200px',
            minWidth: 200,
            bgcolor: 'rgba(251, 191, 36, 0.1)',
            border: '1px solid rgba(251, 191, 36, 0.3)'
          }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <InventoryIcon sx={{ fontSize: 40, color: '#f59e0b', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#f59e0b', mb: 0.5 }}>
                {stats.totalInventory}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                Itens de Acervo
              </Typography>
            </CardContent>
          </Card>

          <Card elevation={0} sx={{
            flex: '1 1 200px',
            minWidth: 200,
            bgcolor: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.3)'
          }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <EventIcon sx={{ fontSize: 40, color: '#3b82f6', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#3b82f6', mb: 0.5 }}>
                {stats.totalEvents}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                Eventos
              </Typography>
            </CardContent>
          </Card>

          <Card elevation={0} sx={{
            flex: '1 1 200px',
            minWidth: 200,
            bgcolor: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.3)'
          }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <RecipeIcon sx={{ fontSize: 40, color: '#22c55e', mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#22c55e', mb: 0.5 }}>
                {stats.totalRecipes}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                Receitas
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Estatísticas por Usuário */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: 'text.primary' }}>
          Médias por Usuário
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Card elevation={0} sx={{ flex: '1 1 180px', minWidth: 180, border: '1px solid rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#f59e0b', mb: 0.5 }}>
                {stats.avgInventoryPerUser.toFixed(1)}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Acervos por usuário
              </Typography>
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ flex: '1 1 180px', minWidth: 180, border: '1px solid rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#3b82f6', mb: 0.5 }}>
                {stats.avgEventsPerUser.toFixed(1)}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Eventos por usuário
              </Typography>
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ flex: '1 1 180px', minWidth: 180, border: '1px solid rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#22c55e', mb: 0.5 }}>
                {stats.avgRecipesPerUser.toFixed(1)}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Receitas por usuário
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Divider sx={{ mb: 4 }} />

      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: 'text.primary' }}>
        Análise Detalhada
      </Typography>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
        <Box sx={{ flex: '1 1 500px', minWidth: 0 }}>
          <Card elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.1)' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Categorias Mais Usadas
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar categoria..."
                inputProps={{ 'aria-label': 'Buscar categoria' }}
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Categoria</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Qtd</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>%</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCategories.slice(0, 50).map((cat) => (
                      <TableRow key={cat.category} hover>
                        <TableCell>{cat.category}</TableCell>
                        <TableCell align="right">{cat.count}</TableCell>
                        <TableCell align="right">
                          {((cat.count / stats.totalInventory) * 100).toFixed(1)}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 500px', minWidth: 0 }}>
          <Card elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.1)' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Palavras Mais Usadas
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar palavra..."
                inputProps={{ 'aria-label': 'Buscar palavra' }}
                value={wordSearch}
                onChange={(e) => setWordSearch(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Palavra</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Ocorrências</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredWords.map((word) => (
                      <TableRow key={word.word} hover>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{word.word}</TableCell>
                        <TableCell align="right">{word.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Indicadores de Uso em Eventos */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: 'text.primary' }}>
        Itens Mais Usados em Eventos
      </Typography>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 500px', minWidth: 0 }}>
          <Card elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.1)' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Top 20 Itens de Acervo em Eventos
              </Typography>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Categoria</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Usos</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.topInventoryItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                          Nenhum item usado em eventos ainda
                        </TableCell>
                      </TableRow>
                    ) : (
                      stats.topInventoryItems.map((item, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{item.itemName}</TableCell>
                          <TableCell>{item.category}</TableCell>
                          <TableCell align="right">{item.usageCount}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 500px', minWidth: 0 }}>
          <Card elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.1)' }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Top 20 Receitas em Eventos
              </Typography>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Receita</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Usos</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stats.topRecipes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                          Nenhuma receita usada em eventos ainda
                        </TableCell>
                      </TableRow>
                    ) : (
                      stats.topRecipes.map((recipe, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{recipe.recipeName}</TableCell>
                          <TableCell align="right">{recipe.usageCount}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};
